import React, { useState, useRef } from 'react';
import { motion, useMotionTemplate, useMotionValue } from 'framer-motion';
import { FaExternalLinkAlt, FaGithub, FaFolder } from 'react-icons/fa';
import BlurText from './ui/BlurText';

const ProjectsSection = ({ projects }) => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  // Debug: Log projects data
  React.useEffect(() => {
    console.log('Projects data:', projects);
  }, [projects]);

  return (
    <section id="projects" className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden bg-slate-950">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-full h-full bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 pointer-events-none"></div>
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-[100px] pointer-events-none"></div>
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-[100px] pointer-events-none"></div>

      <div className="max-w-7xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10 sm:mb-16 md:mb-20"
        >
          <motion.div
            className="inline-flex items-center space-x-2 bg-slate-800/50 border border-slate-700/50 px-3 sm:px-4 py-1 sm:py-1.5 rounded-full mb-4 sm:mb-6 backdrop-blur-sm"
            whileHover={{ scale: 1.05 }}
          >
            <FaFolder className="text-cyan-400 text-xs" />
            <span className="text-xs font-mono text-slate-300 uppercase tracking-wider">Our Work</span>
          </motion.div>

          <BlurText
            text="Featured Projects"
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black mb-4 sm:mb-6 text-center tracking-tight text-white"
            childClassName="text-transparent bg-clip-text bg-gradient-to-b from-white to-slate-400"
            delay={150}
            animateBy="words"
            direction="top"
          />
          <p className="text-slate-400 text-sm sm:text-base md:text-lg max-w-2xl mx-auto leading-relaxed px-4">
            Innovative solutions built by our talented developers. Pushing boundaries with code.
          </p>
        </motion.div>

        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
        >
          {projects && projects.length > 0 ? (
            projects.map((project) => (
              <ProjectCard key={project.projectid} project={project} />
            ))
          ) : (
            <div className="col-span-full">
              <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-12 text-center backdrop-blur-sm">
                <FaGithub className="text-6xl text-slate-700 mx-auto mb-4" />
                <p className="text-slate-500 text-lg">No projects available yet. Stay tuned!</p>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
};

const ProjectCard = ({ project }) => {
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  function handleMouseMove({ currentTarget, clientX, clientY }) {
    const { left, top } = currentTarget.getBoundingClientRect();
    mouseX.set(clientX - left);
    mouseY.set(clientY - top);
  }

  return (
    <motion.div
      className="group relative border border-slate-800 bg-slate-900/50 overflow-hidden rounded-xl"
      onMouseMove={handleMouseMove}
      whileHover={{ y: -5 }}
      transition={{ duration: 0.3 }}
    >
      {/* Spotlight Effect */}
      <motion.div
        className="pointer-events-none absolute -inset-px rounded-xl opacity-0 transition duration-300 group-hover:opacity-100"
        style={{
          background: useMotionTemplate`
            radial-gradient(
              650px circle at ${mouseX}px ${mouseY}px,
              rgba(6, 182, 212, 0.15),
              transparent 80%
            )
          `,
        }}
      />

      {/* Image Container */}
      <div className="relative h-56 overflow-hidden bg-slate-800">
        {project.projectimg ? (
          <img
            src={project.projectimg.startsWith('http') ? project.projectimg : `/files/${project.projectimg}`}
            alt={project.projectname}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 group-hover:rotate-1"
            onError={(e) => {
              console.error('Failed to load image:', project.projectimg);
              e.target.onerror = null;
              e.target.style.display = 'none';
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-slate-800">
            <FaGithub className="text-6xl text-slate-700" />
          </div>
        )}

        {/* Overlay Gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/40 to-transparent opacity-80" />

        {/* Floating Badge */}
        <div className="absolute top-4 right-4 translate-y-[-10px] opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
          <span className="bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 px-3 py-1 rounded-full text-[10px] font-mono uppercase tracking-widest backdrop-blur-md">
            Featured
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="relative p-6">
        <h3 className="text-xl font-bold text-slate-100 mb-2 group-hover:text-cyan-400 transition-colors">
          {project.projectname}
        </h3>
        <p className="text-slate-400 text-sm mb-4 line-clamp-3 leading-relaxed h-[4.5em]">
          {project.projectdescription || 'An innovative project showcasing modern development practices and cutting-edge technology.'}
        </p>

        {/* Built By Section */}
        {project.students && project.students.length > 0 && (
          <div className="mb-4 pb-4 border-b border-slate-800/50">
            <p className="text-xs text-slate-500 font-mono uppercase tracking-wider mb-2">Built By</p>
            <div className="flex flex-wrap items-center gap-2">
              {project.students.map((student) => {
                const StudentContent = (
                  <div className="flex items-center space-x-2 group/student cursor-pointer">
                    {student.studentimage ? (
                      <img
                        src={`/files/${student.studentimage}`}
                        alt={student.studentname}
                        className="w-8 h-8 rounded-full object-cover border-2 border-slate-700 group-hover/student:border-cyan-500 transition-colors"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center border-2 border-slate-700 group-hover/student:border-cyan-500 transition-colors">
                        <span className="text-xs font-bold text-slate-400">{student.studentname?.charAt(0)}</span>
                      </div>
                    )}
                    <div className="flex flex-col">
                      <span className="text-xs font-medium text-slate-300 group-hover/student:text-cyan-400 transition-colors">
                        {student.studentname}
                      </span>
                      {student.batch && (
                        <span className="text-[10px] text-slate-500 font-mono">{student.batch}</span>
                      )}
                    </div>
                  </div>
                );

                return student.linkedin ? (
                  <a
                    key={student.studentid}
                    href={student.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {StudentContent}
                  </a>
                ) : (
                  <div key={student.studentid}>
                    {StudentContent}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="flex items-center justify-between pt-4 border-t border-slate-800/50">
          {project.projectlink ? (
            <a
              href={project.projectlink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 text-sm font-medium text-cyan-400 hover:text-cyan-300 transition-colors group/link"
            >
              <span>View Project</span>
              <FaExternalLinkAlt className="text-xs transition-transform group-hover/link:translate-x-1 group-hover/link:-translate-y-1" />
            </a>
          ) : (
            <span className="text-xs text-slate-600 font-mono">Coming Soon</span>
          )}

          <div className="flex items-center space-x-3 text-slate-500">
            <FaGithub className="text-lg hover:text-white transition-colors cursor-pointer" />
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ProjectsSection;
